import { SITE_URL } from './site.js';
import { de } from '../i18n/de.js';
import { en } from '../i18n/en.js';

const ROOMS = {
  de: {
    catalog: 'Die drei Räume',
    path: '/leistungen',
    rooms: [
      {
        id: 'spiegel-arbeit',
        name: '1:1 Spiegel-Arbeit',
        description: 'Für Entscheiderinnen und Entscheider vor und in entscheidenden Momenten',
      },
      {
        id: 'systeme',
        name: 'Spiegelkraft für Systeme',
        description: 'Für Führungskreise, Projektteams und Familienunternehmen',
      },
      {
        id: 'zirkel',
        name: 'Spiegel für Wirkung und Haltung',
        description: 'Für Führungskräfte und Talente auf dem Weg ins Top-Management',
      },
    ],
  },
  en: {
    catalog: 'The three spaces',
    path: '/en/services',
    rooms: [
      {
        id: 'spiegel-arbeit',
        name: '1:1 Mirror Work',
        description: 'For decision-makers before and during defining moments',
      },
      {
        id: 'systeme',
        name: 'Mirror Work for Systems',
        description: 'For leadership teams, project teams and family businesses',
      },
      {
        id: 'zirkel',
        name: 'Mirror Work for Presence and Integrity',
        description: 'For executives and high potentials on their way into top management',
      },
    ],
  },
};

const PROFILE = {
  de: {
    language: 'de-DE',
    honorificPrefix: 'Prof.in',
    jobTitle: [
      'Rechtsanwältin',
      'Zertifizierte Wirtschaftsmediatorin',
      'Systemisch-integrative Business Coachin',
      'PCM® Coachin',
      'Honorarprofessorin',
    ],
    knowsAbout: [
      'Veränderung verlangt Wahrheit',
      '1:1 Spiegel-Arbeit',
      'Spiegelkraft für Systeme',
      'Spiegel für Wirkung und Haltung',
      'Verhandlungsführung',
      'Führungskonflikte',
      'Unternehmensnachfolge',
      'Familienunternehmen',
      'Wirtschaftsmediation',
      'Vortragstätigkeit',
      'Process Communication Model',
    ],
    areaServed: [
      { '@type': 'City', name: 'München' },
      { '@type': 'Country', name: 'Deutschland' },
    ],
    serviceType: [
      '1:1 Spiegel-Arbeit',
      'Spiegelkraft für Systeme',
      'Spiegel für Wirkung und Haltung',
      'Wirtschaftsmediation',
      'Vortragstätigkeit',
    ],
    blogName: 'Impulse & Perspektiven',
    blogDescription: de.seo.insights.description,
    pages: [
      { type: 'WebPage', name: 'Die drei Räume', url: '/leistungen' },
      { type: 'Blog', name: 'Impulse & Perspektiven', url: '/impulse' },
      { type: 'WebPage', name: 'Stimmen', url: '/stimmen' },
      { type: 'WebPage', name: 'Vorträge', url: '/vortraege' },
      { type: 'WebPage', name: 'Über mich', url: '/ueber-mich' },
    ],
  },
  en: {
    language: 'en-GB',
    honorificPrefix: 'Prof.',
    jobTitle: [
      'Attorney at law',
      'Certified business mediator',
      'Systemic-integrative business coach',
      'PCM® coach',
      'Honorary professor',
    ],
    knowsAbout: [
      'Change demands truth',
      '1:1 Mirror Work',
      'Mirror Work for Systems',
      'Mirror Work for Presence and Integrity',
      'Negotiation',
      'Leadership conflicts',
      'Business succession',
      'Family businesses',
      'Business mediation',
      'Public speaking',
      'Process Communication Model',
    ],
    areaServed: [
      { '@type': 'City', name: 'Munich' },
      { '@type': 'Country', name: 'Germany' },
    ],
    serviceType: [
      '1:1 Mirror Work',
      'Mirror Work for Systems',
      'Mirror Work for Presence and Integrity',
      'Business mediation',
      'Public speaking',
    ],
    blogName: 'Insights & Perspectives',
    blogDescription: en.seo.insights.description,
    pages: [
      { type: 'WebPage', name: 'The three spaces', url: '/en/services' },
      { type: 'Blog', name: 'Insights & Perspectives', url: '/en/insights' },
      { type: 'WebPage', name: 'Testimonials', url: '/en/testimonials' },
      { type: 'WebPage', name: 'Speaking', url: '/en/speaking' },
      { type: 'WebPage', name: 'About', url: '/en/about' },
    ],
  },
};

function pack(lang) {
  return lang === 'en' ? PROFILE.en : PROFILE.de;
}

function copy(lang) {
  return lang === 'en' ? en : de;
}

function offerCatalog(lang) {
  const rooms = ROOMS[lang === 'en' ? 'en' : 'de'];
  return {
    '@type': 'OfferCatalog',
    name: rooms.catalog,
    itemListElement: rooms.rooms.map((room) => ({
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: room.name,
        url: `${SITE_URL}${rooms.path}#${room.id}`,
        description: room.description,
      },
    })),
  };
}

function breadcrumbNode(items) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  };
}

export function breadcrumbJsonLd(items) {
  return {
    '@context': 'https://schema.org',
    ...breadcrumbNode(items),
  };
}

export function webPageJsonLd({ url, title, description, lang, image }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${url}#webpage`,
    url,
    name: title,
    description,
    inLanguage: pack(lang).language,
    isPartOf: { '@id': `${SITE_URL}/#website` },
    about: { '@id': `${SITE_URL}/#person` },
    ...(image
      ? {
          primaryImageOfPage: {
            '@type': 'ImageObject',
            url: image,
          },
        }
      : {}),
  };
}

export function siteJsonLd(lang = 'de') {
  const profile = pack(lang);
  const text = copy(lang);
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Person',
        '@id': `${SITE_URL}/#person`,
        name: 'Michaela Braun',
        honorificPrefix: profile.honorificPrefix,
        alternateName: text.seo.home.title.split('|')[0].trim(),
        jobTitle: profile.jobTitle,
        url: SITE_URL,
        image: `${SITE_URL}/img/michaela_about.png`,
        email: 'mb@michaelabraun.de',
        telephone: '+49 89 88909372',
        knowsLanguage: ['de', 'en'],
        sameAs: [
          'https://www.instagram.com/michaela.regine.braun',
          'https://www.linkedin.com/in/prof-michaela-braun-81810121/',
        ],
        worksFor: { '@id': `${SITE_URL}/#business` },
        knowsAbout: profile.knowsAbout,
      },
      {
        '@type': 'ProfessionalService',
        '@id': `${SITE_URL}/#business`,
        name: 'Michaela Braun',
        url: `${SITE_URL}${lang === 'en' ? '/en' : '/'}`,
        image: `${SITE_URL}/img/michaela_about.png`,
        email: 'mb@michaelabraun.de',
        telephone: '+49 89 88909372',
        description: text.seo.home.description,
        areaServed: profile.areaServed,
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Kirchenstraße 87 b',
          addressLocality: 'München',
          postalCode: '81675',
          addressCountry: 'DE',
        },
        availableLanguage: ['German', 'English'],
        founder: { '@id': `${SITE_URL}/#person` },
        provider: { '@id': `${SITE_URL}/#person` },
        serviceType: profile.serviceType,
        hasOfferCatalog: offerCatalog(lang),
      },
      {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        url: SITE_URL,
        name: 'Michaela Braun',
        alternateName: text.seo.home.title.split('|')[0].trim(),
        description: text.seo.home.description,
        publisher: { '@id': `${SITE_URL}/#person` },
        inLanguage: ['de-DE', 'en-GB'],
        hasPart: profile.pages.map((page) => ({
          '@type': page.type,
          name: page.name,
          url: `${SITE_URL}${page.url}`,
        })),
      },
    ],
  };
}

export function servicesPageJsonLd(lang = 'de') {
  const isEn = lang === 'en';
  const path = isEn ? '/en/services' : '/leistungen';
  const crumb = isEn ? en.services.crumb : de.services.crumb;
  const home = isEn ? '/en' : '/';
  return {
    '@context': 'https://schema.org',
    '@graph': [
      breadcrumbNode([
        { name: 'Home', path: home },
        { name: crumb, path },
      ]),
      offerCatalog(lang),
    ],
  };
}

function impulseExcerpt(body) {
  const first = body
    .split('\n\n')
    .map((block) => block.trim())
    .find((block) => block && !block.startsWith('### '));
  if (!first) return '';
  const text = first.replace(/\s+/g, ' ');
  return text.length > 220 ? `${text.slice(0, 217).trim()}…` : text;
}

export function impulsePageJsonLd(posts, lang = 'de') {
  const isEn = lang === 'en';
  const profile = pack(lang);
  const pagePath = isEn ? '/en/insights' : '/impulse';
  const pageUrl = `${SITE_URL}${pagePath}`;
  const homePath = isEn ? '/en' : '/';
  const section = isEn ? 'Insights' : 'Impulse';

  return {
    '@context': 'https://schema.org',
    '@graph': [
      breadcrumbNode([
        { name: 'Home', path: homePath },
        { name: section, path: pagePath },
      ]),
      {
        '@type': 'Blog',
        '@id': `${pageUrl}#blog`,
        name: profile.blogName,
        url: pageUrl,
        inLanguage: profile.language,
        description: profile.blogDescription,
        publisher: {
          '@type': 'Person',
          name: 'Michaela Braun',
          url: SITE_URL,
        },
        blogPost: posts.map((post) => ({
          '@id': `${pageUrl}#${post.slug}`,
        })),
      },
      ...posts.map((post) => ({
        '@type': 'BlogPosting',
        '@id': `${pageUrl}#${post.slug}`,
        headline: post.title,
        ...(post.kicker ? { alternativeHeadline: post.kicker } : {}),
        datePublished: post.date,
        dateModified: post.date,
        inLanguage: profile.language,
        articleSection: section,
        description: impulseExcerpt(post.body),
        url: `${pageUrl}#${post.slug}`,
        mainEntityOfPage: pageUrl,
        author: {
          '@type': 'Person',
          name: 'Michaela Braun',
          url: SITE_URL,
        },
        publisher: {
          '@type': 'Person',
          name: 'Michaela Braun',
          url: SITE_URL,
        },
      })),
    ],
  };
}
